import React, { useState } from 'react';
import { 
  Eye, 
  Edit, 
  Check, 
  X, 
  FileText, 
  Download, 
  Send, 
  Printer, 
  Copy, 
  Archive, 
  Trash2,
  Mail,
  Share2,
  Clock,
  Euro,
  UserPlus,
  FileCheck
} from 'lucide-react';
import { formaterEuro } from '../services/tarificationService';
import { STATUTS } from '../services/accountingService';

/**
 * Panneau d'actions complet pour la gestion des devis
 * Fournit tous les boutons d'action nécessaires pour le système comptable intégré
 */
const DevisActionsPanel = ({ 
  devis, 
  onAction, 
  userRole = 'admin',
  showDetails = true 
}) => {
  const [actionEnCours, setActionEnCours] = useState(null);

  // Gestionnaire d'actions avec gestion d'état de chargement
  const handleAction = async (action, ...args) => {
    setActionEnCours(action);
    try {
      await onAction(action, ...args);
    } catch (error) {
      console.error(`Erreur lors de l'action ${action}:`, error);
    } finally {
      setActionEnCours(null);
    }
  };

  // Actions disponibles selon le statut du devis
  const getActionsDisponibles = () => {
    const actions = [];

    // Actions communes à tous les statuts
    actions.push(
      {
        id: 'voir',
        label: 'Voir détails',
        icon: Eye,
        color: 'blue',
        description: 'Afficher les détails complets du devis',
        disponible: true
      },
      {
        id: 'dupliquer',
        label: 'Dupliquer',
        icon: Copy,
        color: 'gray',
        description: 'Créer une copie de ce devis',
        disponible: true
      },
      {
        id: 'telecharger',
        label: 'Télécharger PDF',
        icon: Download,
        color: 'green',
        description: 'Télécharger le devis au format PDF',
        disponible: true
      },
      {
        id: 'imprimer',
        label: 'Imprimer',
        icon: Printer,
        color: 'gray',
        description: 'Imprimer le devis',
        disponible: true
      }
    );

    // Actions spécifiques selon le statut
    switch (devis.statut) {
      case STATUTS.DEVIS.BROUILLON:
        actions.push(
          {
            id: 'modifier',
            label: 'Modifier',
            icon: Edit,
            color: 'blue',
            description: 'Modifier le devis en cours',
            disponible: true
          },
          {
            id: 'envoyer',
            label: 'Envoyer au client',
            icon: Send,
            color: 'orange',
            description: 'Envoyer le devis au client par email',
            disponible: true
          },
          {
            id: 'supprimer',
            label: 'Supprimer',
            icon: Trash2,
            color: 'red',
            description: 'Supprimer définitivement le devis',
            disponible: userRole === 'admin',
            confirmation: true
          }
        );
        break;

      case STATUTS.DEVIS.ENVOYE:
        actions.push(
          {
            id: 'relancer',
            label: 'Relancer client',
            icon: Mail,
            color: 'orange',
            description: 'Envoyer un rappel au client',
            disponible: true
          },
          {
            id: 'accepter',
            label: 'Accepter devis',
            icon: Check,
            color: 'green',
            description: 'Accepter le devis et convertir en client',
            disponible: userRole === 'admin',
            confirmation: true
          },
          {
            id: 'refuser',
            label: 'Refuser devis',
            icon: X,
            color: 'red',
            description: 'Refuser le devis avec raison',
            disponible: userRole === 'admin',
            confirmation: true
          },
          {
            id: 'negocier',
            label: 'Négocier',
            icon: Euro,
            color: 'yellow',
            description: 'Ouvrir une négociation avec le client',
            disponible: true
          }
        );
        break;

      case STATUTS.DEVIS.ACCEPTE:
        actions.push(
          {
            id: 'creer_facture',
            label: 'Créer facture',
            icon: FileCheck,
            color: 'green',
            description: 'Créer une facture à partir du devis accepté',
            disponible: userRole === 'admin'
          },
          {
            id: 'archiver',
            label: 'Archiver',
            icon: Archive,
            color: 'gray',
            description: 'Archiver le devis accepté',
            disponible: true
          },
          {
            id: 'partager',
            label: 'Partager',
            icon: Share2,
            color: 'blue',
            description: 'Partager le devis avec d\'autres utilisateurs',
            disponible: true
          }
        );
        break;

      case STATUTS.DEVIS.REFUSE:
        actions.push(
          {
            id: 'reouvrir',
            label: 'Réouvrir',
            icon: Clock,
            color: 'orange',
            description: 'Réouvrir le devis refusé pour modification',
            disponible: userRole === 'admin'
          },
          {
            id: 'analyser',
            label: 'Analyser refus',
            icon: Eye,
            color: 'blue',
            description: 'Analyser les raisons du refus',
            disponible: true
          }
        );
        break;

      case STATUTS.DEVIS.EXPIRE:
        actions.push(
          {
            id: 'renouveler',
            label: 'Renouveler',
            icon: Clock,
            color: 'orange',
            description: 'Renouveler le devis expiré',
            disponible: userRole === 'admin'
          },
          {
            id: 'archiver',
            label: 'Archiver',
            icon: Archive,
            color: 'gray',
            description: 'Archiver le devis expiré',
            disponible: true
          }
        );
        break;
    }

    return actions.filter(action => action.disponible);
  };

  // Obtenir la couleur du statut
  const getCouleurStatut = () => {
    switch (devis.statut) {
      case STATUTS.DEVIS.BROUILLON:
        return 'bg-gray-100 text-gray-800';
      case STATUTS.DEVIS.ENVOYE:
        return 'bg-blue-100 text-blue-800';
      case STATUTS.DEVIS.ACCEPTE:
        return 'bg-green-100 text-green-800';
      case STATUTS.DEVIS.REFUSE:
        return 'bg-red-100 text-red-800';
      case STATUTS.DEVIS.EXPIRE:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obtenir le libellé du statut
  const getLibelleStatut = () => {
    switch (devis.statut) {
      case STATUTS.DEVIS.BROUILLON:
        return 'Brouillon';
      case STATUTS.DEVIS.ENVOYE:
        return 'Envoyé';
      case STATUTS.DEVIS.ACCEPTE:
        return 'Accepté';
      case STATUTS.DEVIS.REFUSE:
        return 'Refusé';
      case STATUTS.DEVIS.EXPIRE:
        return 'Expiré';
      default:
        return devis.statut;
    }
  };

  // Fonction pour formater les dates (gère les différents formats: Date, Timestamp, ISO string)
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      let date;
      
      // Si c'est un Timestamp Firebase
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
        date = dateValue.toDate();
      }
      // Si c'est une chaîne ISO
      else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      }
      // Si c'est déjà un objet Date
      else if (dateValue instanceof Date) {
        date = dateValue;
      }
      // Si c'est un nombre (timestamp)
      else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      }
      else {
        return 'N/A';
      }
      
      // Vérifier que la date est valide
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleDateString('fr-FR');
    } catch (error) {
      console.error('Erreur formatage date:', error, dateValue);
      return 'N/A';
    }
  };

  const actions = getActionsDisponibles();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      {/* En-tête avec informations du devis */}
      {showDetails && (
        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{devis.numero}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{devis.clientNom}</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400 mt-1">
                {formaterEuro(devis.montantTTC)}
              </p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCouleurStatut()}`}>
              {getLibelleStatut()}
            </span>
          </div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
          <div>
            <strong className="text-gray-700 dark:text-gray-300">Créé le:</strong> {formatDate(devis.dateCreation)}
          </div>
          {devis.dateValidite && (
            <div>
              <strong className="text-gray-700 dark:text-gray-300">Validité:</strong> {formatDate(devis.dateValidite)}
            </div>
          )}
        </div>
        </div>
      )}

      {/* Actions principales */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {actions.map((action) => {
          const IconComponent = action.icon;
          const estEnCours = actionEnCours === action.id;
          
          return (
            <button
              key={action.id}
              onClick={() => {
                if (action.confirmation) {
                  if (window.confirm(`Êtes-vous sûr de vouloir ${action.label.toLowerCase()} ce devis ?`)) {
                    handleAction(action.id, devis);
                  }
                } else {
                  handleAction(action.id, devis);
                }
              }}
              disabled={estEnCours}
              className={`
                flex flex-col items-center justify-center p-3 rounded-lg border transition-all duration-200
                hover:shadow-md hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed
                ${estEnCours ? 'animate-pulse' : ''}
                ${
                  action.color === 'blue' ? 'border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                  action.color === 'green' ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300' :
                  action.color === 'red' ? 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-300' :
                  action.color === 'orange' ? 'border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                  action.color === 'yellow' ? 'border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                  'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }
              `}
              title={action.description}
            >
              {estEnCours ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
              ) : (
                <IconComponent size={20} className="mb-1" />
              )}
              <span className="text-xs font-medium text-center leading-tight">
                {estEnCours ? 'En cours...' : action.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Actions rapides pour les statuts spécifiques */}
      {(devis.statut === STATUTS.DEVIS.ENVOYE || devis.statut === STATUTS.DEVIS.ACCEPTE) && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Actions rapides</h4>
          <div className="flex flex-wrap gap-2">
            {devis.statut === STATUTS.DEVIS.ENVOYE && (
              <>
                <button
                  onClick={() => handleAction('envoyer_rappel', devis)}
                  className="flex items-center px-3 py-1 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded text-xs hover:bg-orange-200 dark:hover:bg-orange-900/30"
                >
                  <Mail size={12} className="mr-1" />
                  Rappel
                </button>
                <button
                  onClick={() => handleAction('contacter_client', devis)}
                  className="flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-900/30"
                >
                  <UserPlus size={12} className="mr-1" />
                  Contacter
                </button>
              </>
            )}
            {devis.statut === STATUTS.DEVIS.ACCEPTE && (
              <>
                <button
                  onClick={() => handleAction('planifier_intervention', devis)}
                  className="flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-xs hover:bg-green-200 dark:hover:bg-green-900/30"
                >
                  <Clock size={12} className="mr-1" />
                  Planifier
                </button>
                <button
                  onClick={() => handleAction('creer_contrat', devis)}
                  className="flex items-center px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded text-xs hover:bg-purple-200 dark:hover:bg-purple-900/30"
                >
                  <FileText size={12} className="mr-1" />
                  Contrat
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Indicateur de statut */}
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex justify-between">
          <span>Dernière modification:</span>
          <span>{formatDate(devis.dateModification)}</span>
        </div>
        {devis.acceptePar && (
          <div className="flex justify-between">
            <span>Accepté par:</span>
            <span>{devis.acceptePar}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevisActionsPanel;
